<template>
    <div :class="{ hidden: gGridMode }" class="chart-container" v-cloak>
        <div :class="{ 'full-screen-settings': props.fullScreen }" class="w-full border-t-2 border-blue-600">
            <div class="border bg-tan-200">
                <div class="lg:flex md:justify-between flex-initial flex-row-reverse px-0 sm:px-2">
                    <div class="block">
                        <templates
                            ref="templateRef"
                            @apply="handleApplyTemplate"
                            @created="handleCreatedTemplate"
                            :default-template="gDefaultTemplate"
                            :initial-default="gCurrentTemplate"
                            :initial-templates="props.templates"
                            :initial-public-templates="props.publicTemplates"
                        >
                        </templates>
                    </div>
                    <div class="pb-1 border-t border-gray-200 lg:border-t-0 block">
                        <aggregation
                            @changed="handleApplyAggregation"
                            :section="props.section"
                            :initial-default="props.defaultPeriod"
                            :initial-aggregation="gSettings.aggregation"
                        >
                        </aggregation>
                    </div>
                </div>

                <div class="flex flex-wrap p-1 border-t border-gray-200 md:b-t-0 xs:justify-center print:hidden">
                    <div class="inline-block m-1 xs:mx-0">
                        <settings
                            ref="settingsRef"
                            @apply-settings="handleApplySettings"
                            :initial-settings="gSettings"
                        >
                        </settings>
                    </div>
                    <div class="inline-block m-1 xs:mr-0">
                        <indicators
                            ref="indicatorsRef"
                            @remove="handleRemoveIndicator"
                            @apply-indicators="handleAddIndicator"
                            :chart-instance="gChart"
                        >
                        </indicators>
                    </div>
                    <div class="md:inline-block m-1 hidden lg:visible">
                        <tools
                            ref="drawingtoolRef"
                            @snap="handleSnapToClosest"
                            @add-tool="handleAddTool"
                            :initial-snap="gSettings.snapAnnotationsToPrices"
                            :symbol="props.symbol"
                        >
                        </tools>
                    </div>
                    <div class="inline-block md:ml-auto m-1">
                        <div v-if="!props.fullScreen" class="md:inline-flex xs:hidden sm:hidden">
                            <div @click="handleOpenInNewWindow" class="btn btn-sm btn-blue">Full Screen</div>
                        </div>

                        <reset
                            ref="resetRef"
                            class="md:inline-block xs:hidden sm:hidden mx-1"
                            @reset="handleReset"
                        >
                        </reset>

                        <more-button
                            ref="morebuttonRef"
                            @apply-comparison="handleApplyComparison"
                            @add-to-chart-book="handleSaveNewChartToChartBook"
                            @action="handleChartContextMenuActions"
                            @copyToMonth="handleCopyToMonth"
                            :scale-value="computeScaleSettings"
                            :comparisons="gSettings.comparisons"
                            :section="props.section"
                            :symbol-display="gTitle"
                            :symbol="props.symbol"
                            :chart-books="props.chartBooks"
                        >
                        </more-button>
                    </div>
                    <div class="w-full text-center mt-1 md:hidden">
                        <p class="text-gray-500 italic text-sm">Tools can only be added on larger screens or when a mouse is detected.</p>
                    </div>
                </div>
            </div>
        </div>

        <div ref="tooltipRef" id="chart-tooltip" class="w-full mt-4 text-lg" :class="{ 'hidden': gSettings.priceBox }"></div>
        <div class="w-full mt-2">
            <div id="chart" class="chart-instance" :style="{ height: gHeight }" v-cloak></div>
        </div>

        <div class="w-full mt-4 mb-3">
            <trend-seeker-table id="chartTrendSeekerTable" :symbol="props.symbol"></trend-seeker-table>
        </div>

        <div v-if="!props.fullScreen" class="w-full mt-4 mb-3">
            <div class="bg-tan-200 border-t-2 border-blue-600">
                <div class="border px-3 pt-2">
                    <notes
                        id="chartNotes"
                        label="Chart Notes:"
                        placeholder="Enter the chart notes..."
                        v-model="gNotes"
                        @apply="handleAddNote"
                        :initialNote="gNotes"
                    >
                    </notes>
                </div>
            </div>
        </div>

        <show-chart-books-modal
            @update="handleSaveNewChartToChartBook"
            :chart="gChartRecord"
            :chartBooks="props.chartBooks"
            :title="props.initialTitle"
        >
        </show-chart-books-modal>

        <tool-context-menu :symbol="props.symbol" @update="handleUpdateTool"></tool-context-menu>

        <chart-context-menu @action="handleChartContextMenuActions" :settings="gContextMenu"></chart-context-menu>

        <social-share ref="socialshareRef" @socialShare="handleSocialShare"></social-share>

        <flash-message ref="flashRef" :secondsToDisplay="3"></flash-message>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeMount, inject, nextTick } from 'vue';

import FileSaver from 'file-saver';

import Indicators from './Chart/Indicators';
import MoreButton from './Chart/MoreButton';
import Notes from './Notes';
import Aggregation from './Chart/Aggregation';
import Reset from './Chart/Reset';
import Settings from './Chart/Settings';
import Templates from './Chart/Templates';
import ToolContextMenu from './Chart/ToolContextMenu';
import ChartContextMenu from './Chart/ChartContextMenu';
import ShowChartBooksModal from './Chart/ShowChartBooksModal';
import Tools from './Chart/Tools';
import TrendSeekerTable from './TrendSeekerTable.vue';
import SocialShare from './Chart/SocialShare';
// import ChartBooks from './Chart/ChartBooks'; // CHECK??
// import MakeDefaultChartModal from './Chart/MakeDefaultChartModal'; // NOT USED

// Mixins
// import SaveChartAsImage from './Chart/mixins/SaveChartAsImage'; // INLINEED
// import ExportChartData from './Chart/mixins/ExportChartData'; // INLINEED
import { useRequestInlineTemplate } from '../mixins/RequestInlineTemplate';
import { useChartDefaultValues } from './Chart/mixins/ChartDefaultValues';
import { useInitFeed } from './Chart/mixins/InitFeed';
import { useIsJim } from '../mixins/IsJim';

const props = defineProps({
    // Symbol of the chart
    symbol: {
        type: String,
        required: true
    },
    symbolName: {
        type: String,
        required: true
    },
    // The user's templates
    templates: {
        type: Array,
        required: true
    },
    // Jim's public templates
    publicTemplates: {
        type: Array,
        required: true
    },
    // The user's default template
    userDefaultTemplate: {
        type: Object,
        required: true
    },
    // Chart database record
    initialChartRecord: {
        type: Object,
        required: true
    },
    // The user's chartbooks
    chartBooks: {
        type: Array,
        required: true
    },
    fullScreen: Boolean,
    // The title to display on the chart
    initialTitle: String,
    // The section we're currently under
    section: String,
    // Whether we should be saving every chart or not
    // If we're viewing Jim's, we shouldn't
    initialPreventSave: Boolean,
    // The default aggregation/period settings
    // for the user
    defaultPeriod: Object,
    // If the chart is in realtime/eod mode
    defaultFeedMode: String,
    symbolRoot: String,
});

// global chart iunstances
let gCharts = null;
let gChart = null;

const NEW_STANDALONE_STUDY_HEIGHT = 100;

const $clone = (obj) => JSON.parse(JSON.stringify(obj));

const eventBus = inject('eventBus');

const requestInlineTemplate = useRequestInlineTemplate();
const chartDefaultValues = useChartDefaultValues();
const { initFeed } = useInitFeed(props.defaultFeedMode);
const { isJim } = useIsJim();

const flashRef = ref(null);
const templateRef = ref(null);
const tooltipRef = ref(null);
const indicatorsRef = ref(null);
const settingsRef = ref(null);
const drawingtoolRef = ref(null);
const resetRef = ref(null);
const morebuttonRef = ref(null);
const socialshareRef = ref(null);

const gSettings = ref(normalizeSettings());
const gNotes = ref(props.initialChartRecord.notes || '');
const gHeight = ref(getInitialHeight());

const gDefaultTemplate = ref(props.userDefaultTemplate);
const gCurrentTemplate = ref(props.initialChartRecord.template || null);

const gGridMode= ref(false);
const gChartRecord = ref(props.initialChartRecord);
const gTitle = ref(props.initialTitle);
const gPreventSave = ref(props.initialPreventSave);
const gSaveTimer = ref(null);
const gShareChartUUID = ref(null);
const gShareChartExists = ref(false);

const gContextMenu = ref({
    fullScreen: props.fullScreen,
    display: false,
    position: {
        x: 0,
        y: 0
    }
});

const computeScaleSettings = computed(() => {
    return (settingsRef.value && settingsRef.value.form.scaleValue) || 'priceChange';
});

// open 'Social Share' modal
const cmSocialShare = () => socialshareRef.value.openModal();

// open 'Reset' modal
const cmReset = () => resetRef.value.openModal();

// open 'Indicators' modal
const cmIndicators = () => indicatorsRef.value.openModal();

// open 'Drawing Tool' modal
const cmDrawingTool = () => drawingtoolRef.value.openModal('drawings');
const cmSymbolTool = () => drawingtoolRef.value.openModal('symbols');
const cmCalculatorTool = () => drawingtoolRef.value.openModal('calculators');

// open 'Settings - Display' modal
const cmDisplaySettings = () => settingsRef.value.openDisplaySettings();

// open 'Settings - Scale' modal
const cmDisplayScale = () => settingsRef.value.openScaleSettings();

// open 'Templates' modal
const cmTemplate = () => templateRef.value.openModal();

// open 'Add to Chart Book' modal
const cmAddToChartBook = () => morebuttonRef.value.addToChartBook();
const cmComparisonChart = () => morebuttonRef.value.compareChart();
const cmOpenSaveSettingsAsTemplate = () => templateRef.value.saveNewTemplate();

/**
 * Normalizes the settings from the chartRecord object
 * Retrieving the correct values from ChartDefaultValues mixin
 */
function normalizeSettings() {
    const pInitialState = getInitialState();
    return chartDefaultValues.getInitialValues(pInitialState.template ? JSON.parse(pInitialState.template) : pInitialState);
}

/**
 * Used to bootstrap the chart
 * Attempts to load the chart from the database record, defaulting
 * to an empty symbol
 */
function getInitialState() {
    if (props.initialChartRecord.state) {
        return {
            template: props.initialChartRecord.state,
            symbol: props.symbol
        };
    }

    return { symbol: props.symbol };
}

function getInitialHeight() {
    if (props.fullScreen) {
        return '67vh';
    } else if (props.initialChartRecord.state) {
        let pHeight = 600;
        const pJsonState = JSON.parse(props.initialChartRecord.state);
        const pAdditionalHeight = pJsonState.panes.length - 2;
        if (pAdditionalHeight > 0) {
            pHeight += pAdditionalHeight * NEW_STANDALONE_STUDY_HEIGHT;
        }
        pHeight += 'px';
        return pHeight;
    } else {
        return '600px';
    }
}

function isCompareChart() {
    return gSettings.value.comparisons && Array.isArray(gSettings.value.comparisons.compares);
}

function initCompareChart() {
    const promises = [];

    gSettings.value.comparisons.compares.map(compare => {
        console.log(`Request compare chart symbol ${compare.symbol}`);
        promises.push(requestInlineTemplate.requestChartInlineTemplate(compare.symbol));
    });

    return Promise.all(promises);
}

function getTemplate() {
    if (gDefaultTemplate.value) {
        console.log('Using default template');
        gCurrentTemplate.value = gDefaultTemplate.value;
        return gDefaultTemplate.value.template;
    }
}

function getChartState() {
    const pState = gChart.asTemplate({ main: true });
    return JSON.stringify(pState);
}

function handleSaveChart() {
    if (gPreventSave.value) {
        return;
    }

    console.log('Action taken, saving chart...');

    const pRoute = window.route('interactive-charts.update', gChartRecord.value);

    clearTimeout(gSaveTimer.value);

    gSaveTimer.value = setTimeout(() => {
        const pRequest = {
            state: getChartState(),
            notes: gNotes.value,
            interactive_chart_template_id: gCurrentTemplate.value ? gCurrentTemplate.value.id : null
        };

        axios.put(pRoute, pRequest)
            .then((response) => {
            }).catch((error) => {
                flashRef.value.error('Error saving your chart');
                console.error(error);
            });
    }, 250);

    // the chart is changed, reset the 'share chart UUID'
    gShareChartExists.value = false;
}

function handleCreatedTemplate(pNewTemplate) {
    gCurrentTemplate.value = pNewTemplate;
    handleSaveChart();
}

function applyTemplateToChart(pTemplate, pAnnotations = true, pSaveChart = true) {
    const pOldAggregation = gSettings.value.aggregation;

    gSettings.value = chartDefaultValues.getInitialValues(pTemplate, false);
    gSettings.value.aggregation = $clone(pOldAggregation);

    gChart.load(JSON.stringify(pTemplate), {
        keepOldAnnotations: pAnnotations,
        keepNewAnnotations: pAnnotations,
        // Always force the current aggregation
        // to be kept on the chart
        keepOldAggregation: true,
        keepNewBarWidth: true
    });

    gChart.change({
        id: 'MainPlot',
        context: {
            symbol: props.symbol
        }
    });

    if (pSaveChart) {
        handleSaveChart();
    }
}

/**
 * TODO 1/3/19 Attempting to deprecated this method
 * Replaced with applyTemplateToChart
 */
function handleApplyTemplate(pTemplateRecord, pAnnotations) {
    if (pTemplateRecord.default) {
        gCurrentTemplate.value = null;
        applyTemplateToChart(gChart.getDefaultTemplate(), true);
        return;
    }

    gCurrentTemplate.value = pTemplateRecord;

    const pTemplate = pTemplateRecord.template;
    applyTemplateToChart(pTemplate);
}

function getPeriodFromUnit(pUnit) {
    switch (pUnit) {
        case 'Intraday':
            return 'intraday';
        case 'Day':
            return 'daily';
        case 'Week':
            return 'weekly';
        case 'Month':
            return 'monthly';
        default:
            throw new Error(`Aggregation unit [${pUnit}] not found`);
    }
}

function updateMainPlot() {
    gChart.change({
        id: 'MainPlot',
        context: {
            symbol: props.symbol
        }
    });
}

function getChartByPeriod(pPeriod, pSpec){
    const pRoute = window.route('interactive-charts.definition-by-period-and-spec', { symbol: props.symbol });
    return axios.post(pRoute, { period: pPeriod, spec: pSpec }).then((response) => {
        return response.data;
    });
}

function handleApplyAggregation(pAggregation) {
    // console.log('Applying aggregation:', pAggregation);

    const pPeriod = getPeriodFromUnit(pAggregation.context.unit);
    const pSpec = pAggregation.context.spec === 'Nearest' ? 'nearby' : 'contract';

    getChartByPeriod(pPeriod, pSpec)
        .then((pChartRecord) => {
            gChartRecord.value = pChartRecord;
            gNotes.value = gChartRecord.value.notes;

            // Remove settings/annotations from current chart
            const pCurrentDefinition = gChart.asTemplate({ main: false, annotations: true });

            const pChartOptions = {
                keepOldAnnotations: false,
                keepNewAnnotations: true
            };

            gChart.load(JSON.stringify(pCurrentDefinition), pChartOptions);

            if (gChartRecord.value.state) {
                gChart.load(gChartRecord.value.state, pChartOptions);
                updateMainPlot();
            }

            gSettings.value.aggregation = pAggregation.context;
            gChart.change(pAggregation);

            const pDefaultTemplate = getTemplate();
            if (pDefaultTemplate) {
                applyTemplateToChart(pDefaultTemplate);

                // Re-apply period
                // This seems to be a bug with the chart
                // Expanding/shrinking the period or moving to the left/right
                // will cause the chart to reset it's density
                gChart.change({
                    id: 'Period',
                    context: {
                        density: pDefaultTemplate.display.density
                    }
                });

            } else if (!gChartRecord.value.state) {
                applyTemplateToChart(gChart.getDefaultTemplate(), true);
            }

            handleSaveChart();
            setChartTitle(pPeriod);
        });
}

function applyInitialAggregation() {
    if (gChartRecord.value.period && gChartRecord.value.spec) {
        if (gChartRecord.value.period === 'daily' && gChartRecord.value.spec === 'contract') {
            return;
        }

        const pMapping = {
            'intraday': 'Intraday',
            'daily': 'Day',
            'weekly': 'Week',
            'monthly': 'Month'
        };
        const pSpec = gChartRecord.value.spec === 'nearby' ? 'Nearest' : 'None';
        const pUnit = pMapping[gChartRecord.value.period];

        let pSize = 1;
        if (gChartRecord.value.period === 'intraday' && props.defaultPeriod) {
            pSize = parseInt(props.defaultPeriod.minutes);
        }

        // Bail if we don't have a mapping
        if (!pUnit) return;

        const pAggregation = {
            id: 'Aggregation',
            context: {
                size: pSize,
                spec: pSpec,
                unit: pUnit
            }
        }

        handleApplyAggregation(pAggregation);
    }
}

function removeChartTemplate() {
    gCurrentTemplate.value = null;
}

function handleApplySettings(pActions) {
    console.log('Applying settings', pActions);
    removeChartTemplate();
    gChart.change(...pActions);
    handleSaveChart();
}

function subtractFromHeight() {
    console.log('Subtracting from chart height');
    gHeight.value = parseInt(gHeight.value) - NEW_STANDALONE_STUDY_HEIGHT + 'px';
}

function handleRemoveIndicator(pIndex, pIndicator, pIsApplyChange = true) {
    const pAction = {
        id: 'Plot',
        context: {
            id: 'Delete',
            type: 'Study',
            index: pIndex
        }
    }

    if (pIndicator.placement === 'standalone') {
        subtractFromHeight();
    }

    removeChartTemplate();

    if (pIsApplyChange) {
        gChart.change(pAction);
    }

    handleSaveChart();
}

function handleRemoveIndicatorFromCardPlot(pEventName, pContext) {
    const pIndicator = pContext.plot;
    handleRemoveIndicator(pIndicator.index, pIndicator, false);
}

function handleGridMode(pNewGridMode) {
    gGridMode.value = pNewGridMode;
}

/**
 * When the card "plot" is clicked for a particular indicator
 */
function handleOpenIndicatorModal(pEventName, pIndicator) {
    if (pIndicator && pIndicator.plot && pIndicator.plot.studyId) {
        indicatorsRef.value.showExistingIndicator(pIndicator.plot, pIndicator.plot.index);
    } else {
        morebuttonRef.value.compareChart()
    }
}

function handleShowContextMenu(pEventName, pContext) {
    gContextMenu.value.display = true;
    gContextMenu.value.position.x = pContext.offset.x;
    gContextMenu.value.position.y = pContext.offset.y;

    var pHandleMouseClick = (pEvent) => {
        // Check if we should close the context menu if click is outside context menu
        if (pEvent.target) {
            gContextMenu.value.display = false;
            document.body.removeEventListener('click', pHandleMouseClick);
            document.oncontextmenu = () => {
                return true;
            };
        }
    };

    document.body.addEventListener('click', pHandleMouseClick);
    document.oncontextmenu = () => {
        return false;
    };
}

function handleUpdateTool(pAction) {
    console.log('Updating tool with attributes', pAction);
    gChart.change(pAction);
}

function handleAddTool(pName, pTraits, pValue) {
    console.log(`Applying annotation: [${pName}]`);
    if (pTraits) {
        console.log(`With traits [${JSON.stringify(pTraits)}]`)
    }
    if (pValue) {
        console.log(`With value [${JSON.stringify(pValue)}]`)
    }
    gChart.annotate(pName, pTraits, pValue);
}

function handleSnapToClosest(snapAnnotationsToPrices) {
    console.log(snapAnnotationsToPrices);
    gChart.change({
        id: 'Scale',
        context: {
            snapAnnotationsToPrices
        }
    });
}

// Revisit this later
// Quickly hack this fucking shit in
// Ripped off market-data-ui
function handleUpdateTooltip(pEventName, pEvent) {
    const pFormatValueAsCell = (pName, pValue, pColor) => {
        return `<span class="field-name">${pName}: </span><span class="field-value">${pValue}</span>`;
    }

    const pMainPlot = pEvent.panes[0].plots.find(pPlot => pPlot.isMain);
    const pSymbol = `<span class="value-title">${pMainPlot.name}</span>`;
    const pTimestamp = `<span class="value-timestamp">${pEvent.date} ${pEvent.time || ""}</span>`;
    const pFieldValues = pMainPlot.values.map(mValue => {
        return pFormatValueAsCell(mValue.abbreviation, mValue.value, null);
    }).join(" ");
    const pContent = `${pSymbol} ${pTimestamp} ${pFieldValues}`;
    tooltipRef.value.innerHTML = pContent;
}

function addToHeight() {
    if (!props.fullScreen) {
        console.log('Adding to chart height');
        gHeight.value = parseInt(gHeight.value) + NEW_STANDALONE_STUDY_HEIGHT + 'px';
    }
}

function handleAddIndicator(pIndicator) {
    // console.log('Adding indicator:', pIndicator);
    if (pIndicator.context.placement === 'standalone') {
        addToHeight();
    }

    removeChartTemplate();

    gChart.change(pIndicator);
    handleSaveChart();
}

function showSharedChart(pAction, params) {
    const viaText = "commoditytrends.com";
    let twitterText = 'View my custom ' + params.metadata.title + ' Chart (' + params.metadata.symbolText + ')';
    let shareUrl = window.route('social-share-view', { 'uuid': params.uuid });
    let actionUrl = {
        twitter: 'https://twitter.com/intent/tweet?url=' + window.encodeURIComponent(shareUrl) + '&text=' + window.encodeURIComponent(twitterText) + '&via=' + window.encodeURIComponent(viaText),
        facebook: 'https://www.facebook.com/sharer/sharer.php?u=' + window.encodeURIComponent(shareUrl) + '&via=' + window.encodeURIComponent(viaText),
        linkedin: 'https://www.linkedin.com/sharing/share-offsite/?url=' + window.encodeURIComponent(shareUrl) + '&via=' + window.encodeURIComponent(viaText),
    };

    if (pAction == 'copyLink') {
        flashRef.value.display('Link has been copied!')
    } else {
        flashRef.value.display('The share chart has been created!');
        const params = [
            'height=' + screen.height / 2,
            'width=' + screen.width / 2,
            'fullscreen=no',
            'resizable=yes',
            'scrollbars=yes'
        ].join(', ');
        window.open(
            actionUrl[pAction],
            'shareWindow-' + pAction,
            params
        );
    }
}

function postSocialShare(pChartData, pAction) {
    let twitterSymbol = props.symbol.replace(/^\^/, "").split(/_/).slice(-2)[0];
    let symbolText = '$' + twitterSymbol + (props.symbolRoot ? ' $' + props.symbolRoot : '');

    const params = {
        uuid: gShareChartUUID.value,
        chartData: pChartData,
        metadata: {
            title: gTitle.value,
            userId: window.currentUser.id,
            section: props.section,
            action: pAction,
            symbol: props.symbol,
            symbolName: props.symbolName,
            symbolText: symbolText,
            chartSettings: JSON.stringify(gSettings.value),
        }
    };

    // check if we have created shared chart
    if (gShareChartExists.value) {
        showSharedChart(pAction, params);
    } else {
        const pRoute = window.route('social-share-create');
        axios.post(pRoute, params)
            .then((response) => {
                if (response.data && response.data.uuid) {
                    showSharedChart(pAction, params);
                    gShareChartExists.value = true;
                } else {
                    flashRef.value.error('Error creating share chart');
                }
            }).catch(error => {
                flashRef.value.error('Error creating share chart');
            });
    }
}

function handleSocialShare(action) {
    // generate chart UUID
    if (!gShareChartExists.value || !gShareChartUUID.value) {
        gShareChartUUID.value = ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, a => (a ^ Math.random() * 16 >> a / 4).toString(16));
    }

    // store and get the chart link
    const svgString = gChart.getSVGForExport().replace(/Roboto/g, 'Roboto,Arial');

    var image = new Image();
    image.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));

    image.onload = () => {
        var canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;

        var context = canvas.getContext('2d');
        context.drawImage(image, 0, 0);
        var dataURL = canvas.toDataURL();
        postSocialShare(dataURL, action);
    };

    if (action == 'copyLink') {
        // copy to clipboard
        const el = document.createElement('textarea');
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.value = window.route('social-share-view', { 'uuid': gShareChartUUID.value });
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
    }
}

function handleOpenInNewWindow() {
    const pParams = [
        'height=' + screen.height,
        'width=' + screen.width,
        'fullscreen=yes',
        'resizable=yes',
        'scrollbars=yes'
    ].join(', ');

    const pFullscreenWindow = window.open('?fullscreen=true', '', pParams);

    const pPollForWindowClose = setInterval(() => {
        if (pFullscreenWindow.closed) {
            location.reload();
            window.clearInterval(pPollForWindowClose);
        }
    }, 250);
}

function handleReset(pType) {
    const pTemplate = gChart.getDefaultTemplate();

    templateRef.value.reset();

    // Only remove tools
    if (pType === 'tools') {
        const pToolTemplate = gChart.asTemplate({ main: true });
        applyTemplateToChart(pToolTemplate, false);
    } else if (gDefaultTemplate.value && gDefaultTemplate.value.template) {
        gCurrentTemplate.value = gDefaultTemplate.value;
        applyTemplateToChart(gDefaultTemplate.value.template, false);
        console.log('Reapplying default template');
    } else {
        applyTemplateToChart(pTemplate, false);
    }
}

function handleApplyComparison(pAction, pSettings) {
    gChart.change(...pAction);
    gSettings.value.scaleValue = pSettings.percentChangeScale ? 'percentChange' : 'priceChange';
    gSettings.value.comparisons = pSettings;
    handleSaveChart();
}

function handleSaveNewChartToChartBook(pChartRecord) {
    console.log('Saving new chart into chartbook: ', pChartRecord);

    gChartRecord.value.interactive_chart_book_id = pChartRecord.chartbookId;
    gChartRecord.value.symbol_display = pChartRecord.symbol_display;

    const promises = [];

    if (props.section === 'futures') {
        const pGetChartRoute = window.route('interactive-charts.definition-by-period-and-spec', { symbol: props.symbol });
        const pGetDailyContractChart = axios.post(pGetChartRoute, { period: 'daily', spec: 'contract' }).then((response) => {
            return response.data;
        });
        promises.push(pGetDailyContractChart);
    } else {
        promises.push(new Promise((resolve) => {
            return resolve(gChartRecord.value);
        }));
    }

    return Promise.all(promises)
        .then(([dailyContractChart]) => {
            const pRoute = window.route('interactive-charts.update', { chart: dailyContractChart });

            dailyContractChart.interactive_chart_book_id = pChartRecord.chartbookId;
            dailyContractChart.symbol_display = pChartRecord.symbol_display;

            if (isJim()) {
                dailyContractChart.public = pChartRecord.public || false;
            }

            axios.put(pRoute, dailyContractChart)
                .then((response) => {
                    if (pChartRecord.chartbookId) {
                        flashRef.value.display('Added to My Chart Book');
                    } else {
                        flashRef.value.display('Removed from My Chart Book');
                    }
                })
        })
        .catch((error) => {
            flashRef.value.error();
        });
}

function handleChartContextMenuActions(pAction) {
    const pActionFunction = eval(pAction);
    if (typeof pActionFunction === 'function') {
        pActionFunction();
    } else {
        console.error(`Action function ${pAction} not found.`);
    }
    // this[pAction]();
    // console.log('Action emit: ' + action);
}

function handleCopyToMonth(pSettings) {
    const pRoute = window.route('interactive-charts.copy');
    const pChart = $clone(gChartRecord.value);

    pChart.symbol = pSettings.symbol;
    pChart.state = getChartState();

    if (pSettings.aggregation) {
        // Patch in the new chart aggregation
        const pState = JSON.parse(pChart.state);

        const pPeriodUnitMap = {
            intraday: 'Intraday',
            daily: 'Day',
            weekly: 'Week',
            monthly: 'Month'
        };

        const pSpecMap = {
            contract: 'None',
            nearby: 'Nearest'
        };

        pState.data.aggregation.unit = pPeriodUnitMap[pSettings.aggregation.period];
        pState.data.aggregation.spec = pSpecMap[pSettings.aggregation.spec];

        // if intraday set aggregation size
        pChart.state = JSON.stringify(pState);
        pChart.period = pSettings.aggregation.period;
        pChart.spec = pSettings.aggregation.spec;
    }

    // Used when viewing a chart in My Chart Book
    if (pSettings.symbolDisplay) {
        pChart.symbol_display = pSettings.title;
    }

    console.log('Copying chart', pChart);

    axios.post(pRoute, pChart)
        .then((response) => {
            if ((pSettings.symbol !== props.symbol) || gPreventSave.value) {
                window.location = window.route('interactive-charts.symbol', {
                    symbol: pSettings.symbol,
                    section: props.section
                });
            } else {
                flashRef.value.display('Copied chart');
            }
        }).catch(error => {
            flashRef.value.error('Error copying your chart');
        });
}

function handleAddNote(pNote) {
    handleSaveChart();
}

function setChartTitle(pPeriod) {
    let pTitle;
    let pTitlePeriod;

    if (pPeriod) {
        pTitlePeriod = pPeriod.charAt(0).toUpperCase() + pPeriod.slice(1);
    } else {
        pTitlePeriod = gChartRecord.value.period.charAt(0).toUpperCase() + gChartRecord.value.period.slice(1);
    }

    if (gChartRecord.value.spec === 'nearby') {
        pTitle = `${props.symbolName} ${pTitlePeriod}`;

    } else {
        pTitle = `${props.initialTitle} ${pTitlePeriod}`;
    }

    gTitle.value = pTitle;

    gChart.setTitle({
        text: `${gTitle.value} - CommodityTrends`,
        align: 'left',
        x: 25,
        y: 25,
        style: { "fontSize": "18px", "color": "#888888" },
        floating: true
    });
}

function printWithInfo() {
    print(true);
}

function print(pNeedInfo) {
    var width = '1280';
    var height = '980';

    // get the svg chart
    var chartSvg = gChart.getSVGForExport({ width: width, height: height }).replace(/Roboto/g, 'Roboto,Arial');
    if (typeof (chartSvg) !== 'undefined') {
        // get defs and reuse it
        var origDefs = new DOMParser().parseFromString(chartSvg, 'text/xml').getElementsByTagName('defs')[0];
        if (origDefs) {
            var svgDefs = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svgDefs.setAttribute('id', 'chartSVGDefs');
            svgDefs.setAttribute('width', '0');
            svgDefs.setAttribute('height', '0');
            svgDefs.appendChild(origDefs);
            document.body.insertBefore(svgDefs, document.body.childNodes[0]);
        }

        var chartTrendSeekerTableElm = document.getElementById('chartTrendSeekerTable');
        if (chartTrendSeekerTableElm) {
            var chartTrendSeekerTableCopy = chartTrendSeekerTableElm.cloneNode(true);
        }

        var svgDefs = document.getElementById('chartSVGDefs');
        var appElm = document.getElementById('app');
        var footerElm = document.getElementById('footer');
        var fullScreenChartContainerElm = document.getElementById('fullScreenChartContainer');
        var fullScreenChartTrendSeekerTableElm = document.getElementById('fullScreenChartTrendSeekerTable');

        appElm.classList.add('hidden');
        if (footerElm) {
            footerElm.classList.add('hidden');
        }
        if (fullScreenChartContainerElm) {
            fullScreenChartContainerElm.classList.add('hidden');
        }
        if (fullScreenChartTrendSeekerTableElm) {
            fullScreenChartTrendSeekerTableElm.classList.add('hidden');
        }

        // populate and print
        var chartDiv = document.createElement('div');
        chartDiv.setAttribute('id', 'chartPrintPlaceholder');

        var chartPrintPlaceholderSVG = document.createElement('div');
        chartPrintPlaceholderSVG.setAttribute('id', 'chartPrintPlaceholderSVG');
        chartPrintPlaceholderSVG.innerHTML = chartSvg;
        chartPrintPlaceholderSVG.style.paddingBottom = parseFloat(parseInt(height) / parseInt(width) * 100) + '%';
        chartDiv.appendChild(chartPrintPlaceholderSVG);

        if (pNeedInfo) {
            var notes = gNotes.value;
            var chartPrintPlaceholderInfo = document.createElement('div');
            chartPrintPlaceholderInfo.setAttribute('id', 'chartPrintPlaceholderInfo');
            if (typeof (chartTrendSeekerTableCopy) !== 'undefined') {
                chartPrintPlaceholderInfo.innerHTML += '<div class="mt-4">' + chartTrendSeekerTableCopy.innerHTML + '</div>';
            }
            if (notes) {
                chartPrintPlaceholderInfo.innerHTML += '<div class="mt-8 mb-4"><h4 class="mb-2">Chart Notes:</h4><small>' + notes + '</small></div>';
            }
            chartDiv.appendChild(chartPrintPlaceholderInfo);
        }
        document.body.appendChild(chartDiv);

        window.focus();
        window.print();

        // allow the browser to prepare before reverting
        setTimeout(function () {
            if (typeof (svgDefs) !== 'undefined') {
                svgDefs.remove();
            }
            if (typeof (chartDiv) !== 'undefined') {
                chartDiv.parentNode.removeChild(chartDiv);
            }
            appElm.classList.remove('hidden');
            if (footerElm) {
                footerElm.classList.remove('hidden');
            }
            if (fullScreenChartContainerElm) {
                fullScreenChartContainerElm.classList.remove('hidden');
            }
            if (fullScreenChartTrendSeekerTableElm) {
                fullScreenChartTrendSeekerTableElm.classList.remove('hidden');
            }
        }, 50);
    } else {
        gChart.print();
    }
}

function exportData() {
    const data = gChart.exportData();
    var todayDate = new Date().toISOString().slice(0,10);
    var fileName = props.symbol + '_Interactive_Chart_' + todayDate + '.csv';
    var blob = new Blob([data], {type: 'data:text/csv;charset=utf-8'});
    FileSaver.saveAs(blob, fileName);
}

function saveAsImage() {
    const svgString = gChart.getSVGForExport().replace(/Roboto/g, 'Roboto,Arial');

    var todayDate = new Date().toISOString().slice(0, 10);
    var fileName = props.symbol + '_Interactive_Chart_' + todayDate + '.png';

    var image = new Image();
    image.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));

    image.onload = function () {
        var canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;

        var context = canvas.getContext('2d');
        context.drawImage(image, 0, 0);

        if (typeof window.navigator !== "undefined" && window.navigator.msSaveOrOpenBlob) {
            var blob = canvas.msToBlob();
            FileSaver.saveAs(blob, fileName);
        } else {
            canvas.toBlob(function (blob) {
                FileSaver.saveAs(blob, fileName);
            });
        }
    };
}

function setupEvents() {
    // save the chart when an annotation has been added/changed/moved
    gCharts.PubSub.subscribe(gCharts.Topics.CH_ANNOTATIONCHANGED, handleSaveChart);
    gCharts.PubSub.subscribe(gCharts.Topics.CH_CARDPLOTREMOVED, handleRemoveIndicatorFromCardPlot);
    gCharts.PubSub.subscribe(gCharts.Topics.CH_PANEHEIGHTSCHANGED, handleSaveChart);
    gCharts.PubSub.subscribe(gCharts.Topics.CH_CARDPLOTCLICKED, handleOpenIndicatorModal);
    gCharts.PubSub.subscribe(gCharts.Topics.CH_CHTNEEDSCONTEXTMENU, handleShowContextMenu);
    gCharts.PubSub.subscribe(gCharts.Topics.CH_TOOLTIPCHANGED, handleUpdateTooltip);

    eventBus.on('grid-mode-toggled', handleGridMode);
}

onBeforeMount(() => {
    gCharts = window.Barchart.RealtimeWidgets.Charts;

    setupEvents();

    const gFeed = initFeed();

    const promises = [ gFeed.ready() ];

    // If this is a comparison chart we need the inline template
    // definitions for all comparison symbols loaded before we bootstrap the chart
    if (isCompareChart()) {
        promises.push(initCompareChart())
    }

    Promise.all(promises)
        .then((done) => {
            const pInitialState = getInitialState();

            gChart = gFeed.addChart('chart', pInitialState);

            // TODO This is kind of a hack
            // We are exporting the chart so that nested components can
            // access it without using the global event bus, or nesting a thousand emit's
            // Needed for chart templates
            window.MainChart = gChart;
            window.MainFeed = gFeed;

            const pTemplate = getTemplate();

            if (pTemplate) {
                console.log('Applying template');
                // Apply template, without saving it
                applyTemplateToChart(pTemplate, true, false);
            }

            // This is a "fresh" chart
            if (!gChartRecord.value.state) {
                applyInitialAggregation();
            }

            setChartTitle();
        });
});

onMounted(async () => {
    await nextTick();
});
</script>

<style lang="scss">
.chart-container {
    #chart-tooltip {
        @apply mx-0 bg-yellow-100 px-3 py-2 border border-dotted border-t-2;

        .value-title {
            @apply font-bold;
        }

        .field-name {
            @apply font-bold;
        }
    }

    @apply flex flex-wrap relative;

    &.hidden {
        display: none !important;
    }

    .chart-instance {
        *:focus {
            outline: none;
        }

        .bcharts-card {
            opacity: .8;
        }

        .bcharts-card.bcharts-card-first {
            top: 50px !important;
        }

        .plot {
            // display: inline-block;
            display: inline-flex !important;
            flex-wrap: wrap;
            margin-right: 0.35rem;
            background: #f2f2f2;
            @apply p-1 mb-1 rounded;

            .value-title,
            .field-name,
            .field-value {
                @apply text-sm;
            }

            .field-value {
                margin-left: 0.125rem;
                text-wrap: nowrap;
            }

            .curve-color {
                margin-left: 0.45rem;
                margin-right: 2px;
                margin-bottom: 2px;
                align-self: center;
            }

            .value-title {
                @apply mr-1 cursor-pointer;
            }
        }

        .move-pane {
            display: inline-block;
            @apply p-1 mr-1 cursor-pointer rounded;
            background: #f2f2f2;
        }

        .delete-plot {
            @apply p-1 mr-1 cursor-pointer;

            svg {
                fill: #e3342f;
            }
        }
    }

    #chart-tooltip.hidden {
        display: none;
    }

    .full-screen-settings.chart-settings {
        width: 100%;
        margin: 0;
    }

    .button-group {
        position: absolute;
        bottom: 5px;
        right: 5px;
        z-index: 2;
    }

    .brown-bar {
        background-color: #B18F52;
        min-height: 36px;
        padding: 5px;
    }

    .right-section {
        float: right;
    }

    .full-screen-container {
        position: absolute;
        height: 100%;
        width: 100%;
    }

    .bcharts-card {
        display: flex !important;
        flex-wrap: wrap;
    }

    .bcharts-card,
    .highcharts-title,
    .highcharts-tooltip>* {
        font-family: Roboto, sans-serif !important;
    }

    g.highcharts-tooltip {
        opacity: 0 !important;
    }

    div.highcharts-tooltip span {
        top: 10px !important;
        left: 0 !important;
        margin: 0 !important;
        background: rgba(255, 255, 255, 0.9) !important;
        padding: 10px !important;
        @apply shadow border border-black;

        .values-table {
            .field-name {
                @apply font-bold pr-1;
            }
        }
    }

    @screen xs {
        .highcharts-title {
            font-size: 13px !important;
        }
    }

    @screen sm {
        .highcharts-title {
            font-size: 16px !important;
        }
    }

    @screen md {
        .highcharts-title {
            font-size: 18px !important;
        }
    }
}</style>
